import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wrench, ArrowRight, Star, Briefcase, Shield, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useServiceProviderStore } from '@/stores/service-provider';

const TIER_LABELS: Record<string, { label: string; color: string }> = {
  registered: { label: 'Registered', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  certified: { label: 'Certified', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  qualified_partner: { label: 'Qualified Partner', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
  ambassador: { label: 'Ambassador', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
};

const SPECIALIZATION_LABELS: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  security: 'Security',
  networking: 'Networking',
  solar: 'Solar',
  av: 'A/V',
  iot: 'IoT / Smart Home',
  design: 'Design',
  lighting: 'Lighting',
  landscaping: 'Landscaping',
  automation: 'Automation',
  general: 'General',
  marketing: 'Marketing',
  sales: 'Sales',
  consulting: 'Consulting',
  business_strategy: 'Business Strategy',
  accounting: 'Accounting',
  it_services: 'IT Services',
  web_development: 'Web Development',
  software: 'Software',
  cybersecurity: 'Cybersecurity',
  data_analytics: 'Data Analytics',
  graphic_design: 'Graphic Design',
  content_creation: 'Content Creation',
  photography: 'Photography',
  video_production: 'Video Production',
  other: 'Other',
};

export const ServiceProviderTab: React.FC = () => {
  const isServiceProvider = useAuthStore((s) => s.isServiceProvider);

  if (isServiceProvider) {
    return <ServiceProviderProfileSummary />;
  }

  return <BecomeServiceProviderCTA />;
};

/** @deprecated Use ServiceProviderTab instead */
export const InstallerTab = ServiceProviderTab;

const BecomeServiceProviderCTA: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-primary/10 p-4 mb-4">
          <Wrench className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Become a Service Provider</h3>
        <p className="text-muted-foreground max-w-md mb-6">
          Set up a service provider profile to create and configure client projects for your customers.
          Manage service engagements, hand off pre-built dashboards, and grow your business.
        </p>
        <Button onClick={() => navigate('/service-provider/setup')} size="lg">
          Get Started
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

const ServiceProviderProfileSummary: React.FC = () => {
  const navigate = useNavigate();
  const { profile, isLoading, fetchProfile } = useServiceProviderStore();

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (isLoading && !profile) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return <BecomeServiceProviderCTA />;
  }

  const tier = TIER_LABELS[profile.certificationTier] || TIER_LABELS.registered;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Service Provider Profile</CardTitle>
              <CardDescription>
                {profile.businessName || 'Your service provider profile'}
              </CardDescription>
            </div>
            <Badge className={tier.color}>{tier.label}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Specializations */}
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Specializations</p>
            <div className="flex flex-wrap gap-2">
              {profile.specializations.map((specialization) => (
                <Badge key={specialization} variant="secondary">
                  {specialization === 'other' && profile.otherSpecialization
                    ? profile.otherSpecialization
                    : (SPECIALIZATION_LABELS[specialization] || specialization)}
                </Badge>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <Briefcase className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-semibold">{profile.activeEngagementCount}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <Shield className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-semibold">{profile.completedEngagements}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <Star className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-semibold">
                {profile.ratings.count > 0 ? profile.ratings.average.toFixed(1) : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Rating</p>
            </div>
          </div>

          {/* Action */}
          <div className="pt-2">
            <Button onClick={() => navigate('/service-provider/dashboard')} className="w-full">
              Go to Service Provider Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
